import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client';
import { getVendor } from '../api/masterdata';
import { inFlight, knownMissing, knownNames, lookupQueue } from './vendorNameCache';

/** Anything with a vendor account and the company that account belongs to. */
interface HasVendor {
  vendorAccount: string | null;
  companyCode: string | null;
}

const NO_ROWS: never[] = [];

/** At most this many vendor lookups in flight at once, however long the list. */
const MAX_CONCURRENT_LOOKUPS = 4;

/** Vendors are per-company in D365, so the company is part of the identity. */
function vendorNameKey(companyCode: string, vendorAccount: string): string {
  return `${companyCode}\u0000${vendorAccount}`;
}

async function withSlot<T>(task: () => Promise<T>): Promise<T> {
  if (lookupQueue.running >= MAX_CONCURRENT_LOOKUPS) {
    await new Promise<void>((resolve) => lookupQueue.waiting.push(resolve));
  }
  lookupQueue.running += 1;
  try {
    return await task();
  } finally {
    lookupQueue.running -= 1;
    lookupQueue.waiting.shift()?.();
  }
}

/** The name, `null` when there is none to be had; REJECTS on a failure worth retrying later. */
function lookupVendorName(companyCode: string, vendorAccount: string): Promise<string | null> {
  const key = vendorNameKey(companyCode, vendorAccount);
  const cached = knownNames.get(key);
  if (cached !== undefined) return Promise.resolve(cached);
  if (knownMissing.has(key)) return Promise.resolve(null);
  const pending = inFlight.get(key);
  if (pending) return pending;

  const started = withSlot(async () => {
    try {
      const vendor = await getVendor(vendorAccount, companyCode);
      if (!vendor.name) return null;
      knownNames.set(key, vendor.name);
      return vendor.name;
    } catch (error: unknown) {
      // 404 is an answer (no such vendor in that company). Anything else — D365 down, a
      // network blip — may be gone by the next load, so it is not remembered.
      if (error instanceof ApiError && error.status === 404) {
        knownMissing.add(key);
        return null;
      }
      throw error;
    }
  }).finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, started);
  return started;
}

/**
 * Vendor NAMES for a list of requests.
 *
 * A request stores only the vendor ACCOUNT number — names are D365 master data,
 * never persisted here — so any list that shows a vendor resolves each
 * distinct (company, account) once, live, a few at a time. While that loads, or
 * if D365 cannot answer, the account number is shown rather than nothing; a
 * lookup that FAILED (as opposed to finding nothing) is retried the next time
 * the list changes. Read the result with `vendorLabel`.
 */
export function useVendorNames(
  requests: readonly HasVendor[] = NO_ROWS,
): ReadonlyMap<string, string> {
  const [names, setNames] = useState<ReadonlyMap<string, string>>(new Map());
  const asked = useRef(new Set<string>());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    for (const { vendorAccount, companyCode } of requests) {
      if (!vendorAccount || !companyCode) continue;
      const key = vendorNameKey(companyCode, vendorAccount);
      if (asked.current.has(key)) continue;
      asked.current.add(key);
      lookupVendorName(companyCode, vendorAccount).then(
        (name) => {
          if (name && mounted.current) setNames((prev) => new Map(prev).set(key, name));
        },
        () => {
          asked.current.delete(key);
        },
      );
    }
  }, [requests]);
  return names;
}

/** What a list cell should say for this request's vendor. */
export function vendorLabel(names: ReadonlyMap<string, string>, request: HasVendor): string {
  if (!request.vendorAccount) return '—';
  return (
    names.get(vendorNameKey(request.companyCode ?? '', request.vendorAccount)) ??
    request.vendorAccount
  );
}
